import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit"
import { APIError, AppThunkAPIType } from "../../../app/appTypes"
import { RootState } from "../../../app/store"
import { getWithAuth, postWithAuth, putWithAuth } from "../../../http"
import { logout } from "../../user/userSlice"

export interface CompanyDocumentNumberRule {
    id: number
    companyId: number
    documentType: string
    pattern: string
    formatDescription: string | null
    exampleNumber: string | null
    isActive: boolean
}

export interface SaveCompanyDocumentNumberRule {
    documentType: string
    pattern: string
    formatDescription: string | null
    exampleNumber: string | null
}

export const loadCompanyDocumentNumberRules = createAsyncThunk<CompanyDocumentNumberRule[], void, AppThunkAPIType>(
    "companyDocumentNumberRules/load",
    async (_, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: "api/company-document-number-rules?includeInactive=true" }))
        if (getWithAuth.fulfilled.match(result)) return result.payload as CompanyDocumentNumberRule[]
        return rejectWithValue(result.payload ?? { kind: "unknown" })
    }
)

export const createCompanyDocumentNumberRule = createAsyncThunk<CompanyDocumentNumberRule, SaveCompanyDocumentNumberRule, AppThunkAPIType>(
    "companyDocumentNumberRules/create",
    async (dto, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({ url: "api/company-document-number-rules", payload: dto }))
        if (postWithAuth.fulfilled.match(result)) return result.payload as CompanyDocumentNumberRule
        return rejectWithValue(result.payload ?? { kind: "unknown" })
    }
)

export const updateCompanyDocumentNumberRule = createAsyncThunk<CompanyDocumentNumberRule, { id: number; dto: SaveCompanyDocumentNumberRule }, AppThunkAPIType>(
    "companyDocumentNumberRules/update",
    async ({ id, dto }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({ url: `api/company-document-number-rules/${id}`, payload: dto }))
        if (putWithAuth.fulfilled.match(result)) return result.payload as CompanyDocumentNumberRule
        return rejectWithValue(result.payload ?? { kind: "unknown" })
    }
)

export const setCompanyDocumentNumberRuleActive = createAsyncThunk<CompanyDocumentNumberRule, { id: number; active: boolean }, AppThunkAPIType>(
    "companyDocumentNumberRules/setActive",
    async ({ id, active }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/company-document-number-rules/${id}/${active ? "activate" : "deactivate"}`,
            payload: {}
        }))
        if (putWithAuth.fulfilled.match(result)) return result.payload as CompanyDocumentNumberRule
        return rejectWithValue(result.payload ?? { kind: "unknown" })
    }
)

interface RulesState {
    items: CompanyDocumentNumberRule[]
    status: "empty" | "loading" | "loaded" | "error"
    error?: APIError
}

const initialState: RulesState = { items: [], status: "empty" }

const rulesSlice = createSlice({
    name: "companyDocumentNumberRules",
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(loadCompanyDocumentNumberRules.pending, state => {
                state.status = "loading"
                state.error = undefined
            })
            .addCase(loadCompanyDocumentNumberRules.fulfilled, (state, action) => {
                state.items = action.payload
                state.status = "loaded"
            })
            .addCase(loadCompanyDocumentNumberRules.rejected, (state, action) => {
                state.status = "error"
                state.error = action.payload ?? { kind: "unknown" }
            })
            .addCase(logout.fulfilled, () => initialState)
            .addMatcher(
                isAnyOf(
                    createCompanyDocumentNumberRule.fulfilled,
                    updateCompanyDocumentNumberRule.fulfilled,
                    setCompanyDocumentNumberRuleActive.fulfilled
                ),
                (state, action) => {
                    const rule = action.payload
                    const index = state.items.findIndex(item => item.id === rule.id)
                    if (index === -1) state.items.push(rule)
                    else state.items[index] = rule
                }
            )
    }
})

export const selectCompanyDocumentNumberRules = (state: RootState) => state.companyDocumentNumberRules
export default rulesSlice.reducer
